import { asArray, asNumber, asOptionalString, asRecord, asString } from './common';

/** Full factory record for the Fabryki master-data page (GET /api/factories). */
export interface FactoryDto {
  id: string;
  name: string;
  address?: string;
  taxId?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  notes?: string;
  /** Present on list rows only; `undefined` means the count was not loaded. */
  supplierOrderCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface FactoryInput {
  name: string;
  address: string | null;
  taxId: string | null;
  contactPerson: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
}

export function normalizeFactory(raw: unknown): FactoryDto | null {
  const record = asRecord(raw);
  if (!record) return null;

  return {
    id: asString(record.id),
    name: asString(record.name),
    address: asOptionalString(record.address),
    taxId: asOptionalString(record.taxId),
    contactPerson: asOptionalString(record.contactPerson),
    email: asOptionalString(record.email),
    phone: asOptionalString(record.phone),
    notes: asOptionalString(record.notes),
    supplierOrderCount: record.supplierOrderCount === undefined || record.supplierOrderCount === null
      ? undefined
      : asNumber(record.supplierOrderCount),
    createdAt: asOptionalString(record.createdAt),
    updatedAt: asOptionalString(record.updatedAt),
  };
}

export function normalizeFactories(raw: unknown): FactoryDto[] {
  return asArray(raw, (item) => normalizeFactory(item)).filter(
    (factory): factory is FactoryDto => factory !== null && factory.id !== '',
  );
}

const trimmedOrNull = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

/** Form values → API payload; empty optional fields are sent as null so they clear on edit. */
export function toFactoryInput(values: Record<string, unknown>): FactoryInput {
  return {
    name: typeof values.name === 'string' ? values.name.trim() : '',
    address: trimmedOrNull(values.address),
    taxId: trimmedOrNull(values.taxId),
    contactPerson: trimmedOrNull(values.contactPerson),
    email: trimmedOrNull(values.email),
    phone: trimmedOrNull(values.phone),
    notes: trimmedOrNull(values.notes),
  };
}
